<template>
  <div
    :ref="`media-video-wrapper-${downloadId}`"
    class="mc-vehicle-media__media-video-item-wrapper"
  >
    <VideoPlayer
      v-if="getFormatVideo === 'mp4'"
      :ref="refsNameVideoPlayer"
      class="vjs-custom-skin"
      :options="playerInOptions"
      @ready="onInPlayerReady($event)"
      @error="onInPlayerError($event)"
      @waiting="onInPlayerWaiting($event)"
      @loadeddata="onInPlayerLoadedData($event)"
    />
    <video-player-mux
      v-else-if="getFormatVideo === 'ts'"
      :ref="refsNameVideoPlayer"
      class="vjs-custom-skin video-js"
      :src="src"
      :playerId="refsNameVideoPlayer"
      @ready="onInPlayerReady($event)"
      @error="onInPlayerError($event)"
      @waiting="onInPlayerWaiting($event)"
      @loadeddata="onInPlayerLoadedData($event)"
      @ended="onInPlayerEnded($event)"
    />
    <span class="mc-vehicle-media__media-photo-label-camera">
      {{ $t('trackVideo.camera') }} {{ cameraNumber }}
    </span>
    <div class="mc-vehicle-media__media-video-action">
      <span class="mc-vehicle-media__media-video-action-play-state">
        <i v-if="isPaused" class="mc-icon icon-fill_play" @click="playVideo" />
        <i v-else class="mc-icon icon-fill_pause" @click="pauseVideo" />
      </span>
      <span
        class="mc-vehicle-media__media-video-action-download"
        @click="isLoadingDownlaod ? undefined : handleClickDownload()"
      >
        <b-spinner v-if="isLoadingDownlaod" small />
        <i v-else class="mc-icon icon-outline_download" />
        <span v-if="isLoadingDownlaod">{{ $t('global.processing') }}</span>
        <span v-else>{{ $t('global.action.download') }}</span>
      </span>
      <span class="mc-vehicle-media__media-video-action-fullscreen" @click="handleClickFullscreen">
        <i v-show="!fullscreen" class="mc-fa icon-fill_fullscreen"></i>
        <i v-show="fullscreen" class="mc-fa icon-fill_exit-fullscreen"></i>
      </span>
    </div>
  </div>
</template>

<script>
import VideoPlayer from '@/components/Video/VideoPlayer.vue'
import VideoPlayerMux from '@/components/Video/VideoPlayerMux.vue'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'

export default {
  components: {
    VideoPlayer,
    VideoPlayerMux,
  },
  mixins: [Helper],
  props: {
    cameraNumber: {
      type: Number,
      required: true,
    },
    src: {
      type: String,
      required: true,
    },
    downloadId: {
      type: Number,
      required: true,
    },
    autoPlay: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isPaused: true,
      fullscreen: false,
      playerInOptions: {
        controls: false,
        controlBar: {
          timeDivider: false,
          durationDisplay: false,
        },
        sources: [
          {
            src: '',
            type: 'video/mp4',
          },
        ],
      },
      isLoadingDownlaod: false,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),

    getFormatVideo() {
      if (this.src) {
        const arrUrl = this.src.split('?')
        const arrFormat = arrUrl[0].split('.')
        const format = arrFormat[arrFormat.length - 1]
        return format.toLowerCase()
      } else {
        return 'mp4'
      }
    },
    refsNameVideoPlayer() {
      return `media-video-${this.downloadId}`
    },
  },
  mounted() {
    if (this.src && this.getFormatVideo === 'mp4') {
      this.setVideoPlayerOptions(this.src)
    }
  },
  methods: {
    async setVideoPlayerOptions(url) {
      this.playerInOptions.sources[0].src = url
      if (this.autoPlay) {
        await this.delay(500)
        this.$refs[this.refsNameVideoPlayer].player.play()
        this.isPaused = false
      }
    },
    onInPlayerReady() {
      if (this.autoPlay) {
        this.$refs[this.refsNameVideoPlayer].player.play()
        this.isPaused = false
      } else {
        this.$refs[this.refsNameVideoPlayer].player.pause()
        this.isPaused = true
      }
      this.$refs[this.refsNameVideoPlayer].player.on('ended', (event) => {
        this.isPaused = true
      })
    },

    onInPlayerError(e) {},

    onInPlayerWaiting() {},

    onInPlayerLoadedData(e) {},

    onInPlayerEnded() {
      this.$refs[this.refsNameVideoPlayer].player.pause()
      this.isPaused = true
    },

    playVideo() {
      this.$refs[this.refsNameVideoPlayer].player.play()
      this.isPaused = false
    },

    pauseVideo() {
      this.$refs[this.refsNameVideoPlayer].player.pause()
      this.isPaused = true
    },

    async convertAndDownload() {
      const path = this.extractPathFromDomain(this.src)
      this.isLoadingDownlaod = true
      try {
        const res = await fetch(process.env.VUE_APP_API_CONVERT_VIDEO + '/' + path, {
          method: 'GET',
        })
        if (!res.ok) throw new Error('Failed to fetch file')
        const blob = await res.blob()
        const downloadUrl = window.URL.createObjectURL(blob)
        let filename = path.split('/')[path.split('/').length - 1]
        const contentDisposition = res.headers.get('Content-Disposition')
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1]
          }
        }
        this.downloadFile(downloadUrl, filename)
      } catch (error) {
        this.$mcToast.error('Failed convert video')
      } finally {
        this.isLoadingDownlaod = false
      }
    },

    handleClickDownload() {
      const hasAccessConvertVideo = this.hasAccess(['b3q-trackvision-convert-video-download:read'])
      if (hasAccessConvertVideo) {
        this.convertAndDownload()
      } else {
        const link = document.createElement('a')
        link.href = this.src
        link.setAttribute('download', null)
        link.click()
        link.remove()
      }
    },

    async handleClickFullscreen() {
      this.fullscreen = !this.fullscreen
      await this.$nextTick()
      const figureDom = this.$refs[`media-video-wrapper-${this.downloadId}`]
      figureDom.classList.toggle('mc-vehicle-media__media-fullscreen')
      if (this.fullscreen) {
        var elem = document.documentElement

        if (elem.requestFullscreen) {
          elem.requestFullscreen()
        } else if (elem.webkitRequestFullscreen) {
          /* Safari */
          elem.webkitRequestFullscreen()
        } else if (elem.msRequestFullscreen) {
          /* IE11 */
          elem.msRequestFullscreen()
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.webkitExitFullscreen) {
          /* Safari */
          document.webkitExitFullscreen()
        } else if (document.msExitFullscreen) {
          /* IE11 */
          document.msExitFullscreen()
        }
      }
    },
  },
}
</script>
